import {isFieldActive, translate} from "helpers/common"
import {Model} from "Model"
import {Settings} from "types/settings"

export type TemplateUserDetails = {
    userDetails?: any
    profile?: any
    campusDetails?: any
    admissionAdvisor?: any
    finAidAdvisor?: any
    academicAdvisor?: any
    studentLedgerStats?: any
    studentOverallStats?: any
    [key: string]: any
}

export type TemplateOption = {
    id: string
    name: string
    isInput?: boolean
    fieldLabel?: string
    fieldType?: string
    fieldInputType?: "text" | "date" | "time" | "number"
    canWriteOnlyInTemplate?: boolean
}

export type TemplateFieldGroup = {
    label: string
    options: TemplateOption[]
}

const STUDENT_DATA_TEMPLATE_FIELDS: TemplateFieldGroup[] = [
    {
        label: translate("tasks:studentData.userInfo"),
        options: [
            {id: "fullName", name: translate("tasks:studentData.fullName")},
            {id: "firstName", name: translate("tasks:studentData.firstName")},
            {id: "lastName", name: translate("tasks:studentData.lastName")},
            {id: "middleName", name: translate("tasks:studentData.middleName")},
            {id: "nickNamePreferedName", name: translate("tasks:studentData.nickNamePreferedName")},
            {id: "email", name: translate("tasks:studentData.primaryEmail")},
            {id: "phone", name: translate("tasks:studentData.primaryPhone")},
            {id: "gender", name: translate("tasks:studentData.gender")},
            {id: "birthDate", name: translate("tasks:studentData.dob")},
            {id: "photo", name: translate("tasks:studentData.photo")},
            {id: "ssn", name: translate("tasks:studentData.ssn")},
            {id: "address", name: translate("tasks:studentData.address")},
            {id: "city", name: translate("tasks:studentData.city")},
            {id: "state", name: translate("tasks:studentData.state")},
            {id: "postalCode", name: translate("tasks:studentData.postalCode")},
            {id: "country", name: translate("tasks:studentData.country")},
            {id: "campuses", name: translate("tasks:studentData.campus")},
            {id: "campusFafsaCodes", name: translate("tasks:studentData.campusFafsaCodes")},
            {id: "campusOfficialName", name: translate("tasks:studentData.campusOfficialName")},
            {id: "campusAddress", name: translate("tasks:studentData.campusAddress")},
            {id: "campusEmail", name: translate("tasks:studentData.campusEmail")},
            {id: "campusPhone", name: translate("tasks:studentData.campusPhone")},
            {id: "campusCity", name: translate("tasks:studentData.campusCity")},
            {id: "campusState", name: translate("tasks:studentData.campusState")},
            {id: "campusPostalCode", name: translate("tasks:studentData.campusPostalCode")},
            {id: "campusWebsiteUrl", name: translate("tasks:studentData.campusUrl")},
            {id: "program", name: translate("tasks:studentData.major")},
            {id: "customProfileId", name: translate("tasks:studentData.studentId")},
            {id: "customUserId", name: translate("tasks:studentData.userId")},
            {id: "startTerm", name: translate("tasks:studentData.startTerm")},
            {id: "term", name: translate("tasks:studentData.currentTerm")},
            {id: "startDate", name: translate("tasks:studentData.startDate")},
            {id: "graduationDate", name: translate("tasks:studentData.actualEndDate")},
            {id: "contractGraduationDate", name: translate("tasks:studentData.contractEndDate")},
            {id: "orientationDate", name: "Orientation Date"},
            {id: "projectedEndDate", name: translate("tasks:studentData.projectedEndDate")},
            {
                id: "naccasGradDate",
                name: translate("tasks:studentData.naccasGradDate"),
                fieldLabel: Settings.GeneralLabel.NaccasGradDate
            },
            {id: "documentsFolder", name: translate("tasks:studentData.documentsFolder")}
        ]
    },
    {
        label: translate("tasks:studentData.admissions"),
        options: [
            {id: "admissionStatus", name: translate("tasks:studentData.admissionStatus")},
            {id: "admissionAdvisor", name: translate("tasks:studentData.admissionAdvisor")},
            {id: "enrollmentStatus", name: translate("tasks:studentData.enrollmentStatus")},
            {id: "admissionAdvisorEmail", name: translate("tasks:studentData.admissionAdvisorEmail")},
            {id: "admissionAdvisorPhone", name: translate("tasks:studentData.admissionAdvisorPhone")},
            {id: "lastDayOfAttendance", name: "Last Day of Attendance"},
            {
                id: "lastDayOfAttendancePlus",
                name: "Last Day of Attendance +",
                fieldType: "field-label-input",
                fieldLabel: "Number of days",
                fieldInputType: "number",
                canWriteOnlyInTemplate: true
            },
            {id: "attendance", name: "Attendance"},
            {id: "missedAttendanceHours", name: "Missed Attendance (Hours)"},
            {id: "missedAttendanceDays", name: "Missed Attendance (Days)"},
            {id: "transferHours", name: "Transfer Hours"},
            {id: "attemptedHours", name: "Attempted Hours"},
            {id: "completedHours", name: "Completed Hours"},
            {id: "remainingHours", name: "Remaining Hours"},
            {id: "distantEduHours", name: "Distant-edu Hours"},
            {id: "scheduledHours", name: "Scheduled Hours"}
        ]
    },
    {
        label: translate("tasks:studentData.finAid"),
        options: [
            {id: "finAidStatus", name: translate("tasks:studentData.financialAidStatus")},
            {id: "finAidAdvisor", name: translate("tasks:studentData.financialAidAdvisor")},
            {id: "efc", name: translate("tasks:studentData.efc")},
            {id: "ISIR", name: translate("tasks:studentData.isirDate")},
            {id: "dependencyStatus", name: translate("tasks:studentData.dependencyStatus")},
            {id: "awardYear", name: translate("tasks:studentData.awardYear")},
            {id: "finAidAdvisorEmail", name: translate("tasks:studentData.finAidAdvisorEmail")},
            {id: "finAidAdvisorPhone", name: translate("tasks:studentData.finAidAdvisorPhone")}
        ]
    },
    {
        label: translate("tasks:studentData.studentAccount"),
        options: [
            {id: "accountBalance", name: translate("tasks:studentData.accountBalance")},
            {id: "paymentPlanBalance", name: translate("tasks:studentData.paymentPlanBalance")},
            {id: "nextPaymentAmount", name: translate("tasks:studentData.nextPaymentAmount")},
            {id: "nextPaymentDueDate", name: translate("tasks:studentData.nextPaymentDueDate")},
            {id: "pastDueAmount", name: translate("tasks:studentData.pastDueAmount")},
            {id: "paymentLinkGeneral", name: translate("tasks:studentData.paymentLinkGeneral")},
            {id: "paymentLinkPlanItem", name: translate("tasks:studentData.paymentLinkPlanItem")}
        ]
    },
    {
        label: translate("tasks:studentData.academics"),
        options: [
            {
                id: "averageAttendancePercentage",
                name: translate("tasks:studentData.attendance"),
                fieldLabel: Settings.AcademicLabel.AttendancePercentage
            },
            {
                id: "cgpa",
                name: translate("tasks:studentData.cgpa"),
                fieldLabel: Settings.AcademicLabel.Cgpa
            },
            {id: "academicAdvisor", name: translate("tasks:studentData.academicAdvisor")},
            {id: "academicAdvisorEmail", name: translate("tasks:studentData.academicAdvisorEmail")},
            {id: "academicAdvisorPhone", name: translate("tasks:studentData.academicAdvisorPhone")},
            {id: "academicShift", name: translate("tasks:studentData.academicShift")},
            {id: "academicPhase", name: translate("tasks:studentData.academicPhase")},
            {id: "academicTrack", name: translate("tasks:studentData.academicTrack")}
        ]
    },
    {
        label: translate("tasks:clientData.label"),
        options: [
            {id: "clientName", name: translate("tasks:clientData.name")},
            {id: "clientLogoUrl", name: translate("tasks:clientData.logoUrl")}
        ]
    },
    {
        label: translate("tasks:studentData.others"),
        options: [
            {id: "todaysDate", name: translate("tasks:studentData.todaysDate")},
            {id: "checkListName", name: "Checklist Name"},
            {id: "checkListItemName", name: "Checklist Item Name"},
            {id: "title", name: "Title"},
            {id: "message", name: "Message"},
            {id: "urlRegister", name: "URL Register"}
        ]
    }
]

export const getStudentDataTemplateFields = (model: Model): TemplateFieldGroup[] =>
    STUDENT_DATA_TEMPLATE_FIELDS.map((group) => ({
        ...group,
        options: group.options.filter((option) => {
            if (option.id === "naccasGradDate") {
                return isFieldActive(model, Settings.GeneralLabel.NaccasGradDate)
            }
            return true
        })
    }))
